/* eslint-disable no-console */

/* ========================================================================== *\
	IMPORTS
\* ========================================================================== */
import Calendar from '../../04-components/calendar/calendar.js';
import MetadataProviderManager from '../../01-helpers/utilities/metadata-providers.js';



/* ========================================================================== *\
	TYPE DEFINITIONS
\* ========================================================================== */
/**
 * @typedef { import('../../01-helpers/utilities/metadata-providers.js').MetadataProvider } MetadataProvider
 */



/* ========================================================================== *\
	PRIVATE VARIABLES
\* ========================================================================== */
const
	defaultWeekend = [0, 6],
	baseCssClass = 'c-datepicker',

	cssClass = {
		day: `${ baseCssClass }__day`,
		dayNumber: `${ baseCssClass }__day-number`,
		headerMonth: `${ baseCssClass }__header-month`,
		isAvailable: 'is-available',
		isRangeDay: 'is-range-day',
		isRangeEnd: 'is-range-end',
		isRangeStart: 'is-range-start',
		isSelected: 'is-selected',
		isToday: 'is-today',
		isWeekend: 'is-weekend',
		monthContainer: `${ baseCssClass }__month ${ baseCssClass }__container ${ baseCssClass }__days`
	},

	headerFormatter = Intl.DateTimeFormat('nl', {
		month: 'long'
	}),

	propertyKeys = {
		metadataManager: Symbol('metadataManager')
	};



/* ========================================================================== *\
	TYPE DEFINITIONS
\* ========================================================================== */
/**
 * @typedef { import('../../04-components/datepicker/datepicker').DateMetadata } DateMetadata
 */



/* ========================================================================== *\
	PRIVATE METHODS
\* ========================================================================== */

/* ---------------------------------- *\
	FORMAT METHODS
\* ---------------------------------- */
/**
 * Returns the HTML to show in the calender header when rendering only a
 * single month.
 *
 * @param {Date} start The month which will be displayed in the calendar.
 *
 * @returns {string} An HTML string with the header text for the calendar.
 */
function formatHeaderSingleMonth(start) {
	const
		formattedDate = headerFormatter.format(start),
		fullYear = start.getFullYear();

	return `<span class="c-datepicker__header-month">${ formattedDate }  </span>${ fullYear }`;
}

/**
 * Returns the HTML to show in the calender header when rendering two or more
 * months at a time
 *
 * @param {Date} start The first month which will be displayed in the calendar.
 * @param {Date} end The last month which will be displayed in the calendar.
 *
 * @returns {string} An HTML string with the header text for the calendar.
 */
function formatHeaderTwoMonths(start, end) {
	const
		startMonth = headerFormatter.format(start),
		startYear = start.getFullYear(),
		endMonth = headerFormatter.format(end),
		endYear = end.getFullYear();

	// When both months are in the same year the result will be a string in the
	// format of: <first month> - <last month>  <year>
	if (startYear === endYear) {
		return `<span class="c-datepicker__header-month">${ startMonth } - ${ endMonth }  </span>${ startYear }`
	}

	// When the months are in different years the result will be a string in the
	// format of: <first month> <year>  -  <last month> <year>
	const
		partStart = `<span class="c-datepicker__header-month">${ startMonth } </span>${ startYear }`,
		partEnd = `<span class="c-datepicker__header-month">  -  ${ endMonth } </span>${ endYear }`;

	return `${partStart}${partEnd}`
}


/* ---------------------------------- *\
	GET ATTRIBUTES
\* ---------------------------------- */
/**
 * Returns an attribute with the price as specified in the metadata. When the
 * provided metadata does not contain a price, an empty string is returned.
 *
 * @param {DateMetadata} day The day for which to return the attribute.
 *
 * @returns {string} A string with the attribute containing the price.
 */
function getAttributePrice(day) {
	return (day.price)
		? `title="${day.price}" data-price="${day.price}"`
		: '';
}

/**
 * Returns an attribute with the timestamp of the date unless the provided day
 * is padding to fill out the first or last week.
 *
 * @param {DateMetadata} day The day for which to return the attribute.
 *
 * @returns {string} A string with the attribute containing the timestamp.
 */
function getAttributeTimestamp(day) {
	return !day.isPadding
		? `data-timestamp="${day.timestamp}"`
		: '';
}

/**
 * Creates a string containing all the attributes to be added to single day in
 * the HTML string
 *
 * @param {DateMetadata} day The day for which to return the attributes.
 *
 * @returns {string} A string containing space separated attributes and
 *          their values.
 */
function getAttributesForDay(day) {
	// Create an array with the results of the individual methods. From the
	// resulting array, remove all items containing an empty string. Join the
	// remaining items together, separating them with a space.
	return ([
		getAttributeTimestamp(day),
		getAttributePrice(day)
	]).filter(attribute => attribute !== '').join(' ');
}


/* ---------------------------------- *\
	GET CSS CLASS
\* ---------------------------------- */
/**
 * Determines whether or not the CSS class for "is available" should be applied
 * on the HTML element representing the provided day.
 *
 * @param {DateMetadata} day The day for which to return the CSS class.
 *
 * @returns {string} Returns the CSS classname for the "is available" class when
 *          the provided day falls on a weekend.
 */
function getCssClassIsAvailable(day) {
	return (day.isAvailable)
		? cssClass.isAvailable
		: '';
}

/**
 * Determines whether or not a CSS class should be applied which identifies as
 * the day belonging to a selection.
 *
 * @param {DateMetadata} day The day for which to return the CSS class.
 *
 * @returns {string} Returns the CSS classname based on what kind of a selected
 *          day it is.
 */
function getCssClassIsSelected(day) {
	const getRangeClass = () => {
		const
			dayIsRange = day.isSelectionStart && day.isSelectionEnd;

		if (dayIsRange) {
			return cssClass.isSelected;
		}

		if (day.isSelectionStart) {
			return cssClass.isRangeStart;
		}

		if (day.isSelectionEnd) {
			return cssClass.isRangeEnd;
		}

		return cssClass.isRangeDay;
	}

	// Padding days never should be highlighted, same goes for days not part of
	// a selection.
	if (
		day.isPadding ||
		!day.isSelected
	) {
		return '';
	}

	return getRangeClass();
}

/**
 * Determines whether or not the CSS class for "is today" should be applied on
 * the HTML element representing the provided day.
 *
 * @param {DateMetadata} day The day for which to return the CSS class.
 *
 * @returns {string} Returns the CSS classname for the "is today" class when
 *          the provided day falls on a weekend.
 */
function getCssClassIsToday(day) {
	// The today class should NOT be applied on padding days and not when it is
	// part of a selection.
	return (day.isToday && !day.isPadding && !day.isSelected)
		? cssClass.isToday
		: '';
}

/**
 * Determines whether or not the CSS class for "is weekend" should be applied on
 * the HTML element representing the provided day.
 *
 * @param {DateMetadata} day The day for which to return the CSS class.
 *
 * @returns {string} Returns the CSS classname for the "is weekend" class when
 *          the provided day falls on a weekend.
 */
function getCssClassIsWeekend(day) {
	return (defaultWeekend).includes(day.dateParts.day)
		? cssClass.isWeekend
		: '';
}

/**
 * Determines which CSS classes are to be applied to the HTML element
 * representing the provided day.
 *
 * @param {DateMetadata} day The day for which to return the CSS classes.
 *
 * @returns {string} Returns the CSS classnames, as a space separated string, to
 *          apply the to element representing the provided day
 */
function getCssClassesForDay(day) {
	// Create an array with the results of the individual methods. From the
	// resulting array, remove all items containing an empty string. Join the
	// remaining items together, separating them with a space.
	return ([
		getCssClassIsAvailable(day),
		getCssClassIsSelected(day),
		getCssClassIsToday(day),
		getCssClassIsWeekend(day)
	]).filter(className => className !== '').join(' ');
}


/* ---------------------------------- *\
	RENDER METHODS
\* ---------------------------------- */
/**
 * Creates an HTML string for the provided day.
 *
 * @param {DateMetadata} day The metadata for the day to render.
 *
 * @returns {string} Returns an HTML string for the provided day.
 */
function renderDay(day) {
	return `<div class="${cssClass.day} ${getCssClassesForDay(day)}" ${ getAttributesForDay(day) }>
		<span class="${ cssClass.dayNumber } ${ getCssClassesForDay(day) }">
			${ day.isPadding ? '' : day.dateParts.date }
		</span>
	</div>`;
}

/**
 * Creates an HTML string with the days to be displayed for the calendar month.
 *
 * @param {Array<DateMetadata>} days The days to be displayed in the month.
 *
 * @returns {string} Returns an HTML string for the provided month.
 */
function renderMonth(days) {
	// Reduce the days array to a single string containing the HTML to display
	// all the days. Wrap them inside a container element, this will help when
	// rendering multiple months in a calendar.
	return `<div class="${cssClass.monthContainer}">
		${ days.reduce((result, day) => `${result}${renderDay(day)}`, '') }
	</div>`;
}

/**
 * Returns the HTML to display the months in the calendar.
 *
 * @param {Array<DateMetadata[]>} months An array where each item is another
 *        array containing the days to be displayed for a specific month.
 *
 * @returns {string} Returns an HTML string containing all the months which were
 *          provided.
 */
function renderMonths(months) {
	// Reduce the array to a single string containing the HTML for all the
	// provided months.
	return months.reduce((result, month) => result + renderMonth(month), '');
}

/**
 * Returns the HTML to display the months in the calendar after enriching the
 * provided months with metadata from the registered metadata providers..
 *
 * @param {Array<DateMetadata[]>} months An array where each item is another
 *        array containing the days to be displayed for a specific month.
 *
 * @returns {string} Returns an HTML string containing all the months which were
 *          provided.
 */
async function renderMonthsWithMetadata(months) {
	// Flatten the array with months to a single array containing the days of
	// all the months.
	const
		flattenedMonths = months.reduce((result, month) => [...result, ...month], [])

	await this[propertyKeys.metadataManager].applyMetadataProviders(flattenedMonths);

	return renderMonths(months);
}



/* ---------------------------------- *\
	SETUP METHODS
\* ---------------------------------- */
/**
 * Setup the dependencies for the class instance.
 *
 * @this CalendarRenderer
 */
function setup() {
	this[propertyKeys.metadataManager] = new MetadataProviderManager();
}



/* ========================================================================== *\
	PUBLIC API
\* ========================================================================== */
class CalendarRenderer {
	/* ====================================================================== *\
		CONSTRUCTOR
	\* ====================================================================== */
	constructor() {
		setup.call(this);
	}



	/* ====================================================================== *\
		PUBLIC METHODS
	\* ====================================================================== */
	/**
	 *
	 * @param {Date[]} months
	 */
	renderHeader(months) {
		const
			start = months[0],
			end = months[months.length - 1];

		if (start.getTime() === end.getTime()) {
			return formatHeaderSingleMonth(start);
		} else {
			return formatHeaderTwoMonths(start, end);
		}
	}

	/**
	 *
	 * @param {Array<DateMetadata[]>} months
	 * @param reason
	 */
	renderDays(months, reason) {
		switch (reason) {
		case Calendar.reason.navigationFinished:
		case Calendar.reason.rendererChanged:
			return renderMonthsWithMetadata.call(this, months);

		default:
			return Promise.resolve(renderMonths(months));
		}
	}

	/**
	 * Adds a metadata provider. The provider will be called to add metadata to
	 * the days to be rendered in the calendar.
	 *
	 * @param {MetadataProvider} provider The metadata provider to add.
	 */
	registerProvider(provider) {
		this[propertyKeys.metadataManager].registerProvider(provider);
	}

	/**
	 * Removes a previously registered metadata provider.
	 *
	 * @param {MetadataProvider} provider The metadata provider to remove.
	 */
	unregisterProvider(provider) {
		this[propertyKeys.metadataManager].unregisterProvider(provider);
	}
}



/* ========================================================================== *\
	EXPORTS
\* ========================================================================== */
export default CalendarRenderer;
